import AppLoader from "../components/AppLoader";
import React, { useEffect, useState } from "react";
import {
  Fade,
  Grow,
  Box,
  Typography,
  Divider,
  Chip,
  Avatar,
  Button,
  Paper,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { api } from "../services/api";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { useAuth } from "../hooks/AuthContext";
import { Navigate, useLocation } from "react-router-dom";  // en vez de useNavigate
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import AppBackground from "../components/layout/AppBackground";


const Billing = () => {
  const { t, i18n } = useTranslation("billing");
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(null);
  const { isAuthenticated, user, authLoading } = useAuth();
  const location = useLocation();
  const isEarlyPitcher = !!user?.is_early_pitcher;
  const isUndeltaUser = user?.source === "undelta";


  useEffect(() => {
    const fetchBilling = async () => {
      try {
        const res = await api.get("/api/user/billing");
        setBilling(res.data);
        console.log("Billing data:", res.data);

      } catch (err) {
        console.error("Error fetching billing:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBilling();
  }, []);

  // Si no esta logueado, lo mandamos al login
  if (authLoading) {
    return <AppLoader message={t("loading")} />;
  }
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname }} // Ahora pasa "/billing"
      />
    );
  }
  // Si NO es Early Pitcher, lo mandamos a Early Pitcher
  if (!user?.is_early_pitcher && !isUndeltaUser) {
    return <Navigate to="/early-pitchers" replace />;
  }
  
  if (loading) return <AppLoader message={t("loading")} />;

  if (!billing) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography>{t("no_data")}</Typography>
      </Box>
    );
  }

  const planExpired = billing.current_subscription
    ? new Date(billing.current_subscription.end_date) < new Date()
    : true;


  return (
    <Fade in timeout={700}>
      <AppBackground
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          px: 3,
        }}
      >
        <Box maxWidth="600px" width="100%">
          <Typography variant="h4" gutterBottom sx={{ py:0, mt: 0, mb: 5 }}>
            {t("billing_title")}
          </Typography>
        </Box>


       {isUndeltaUser && (
          <Grow in timeout={725}>
            <Box maxWidth="600px" width="100%" sx={{ mb: 3 }}>
              <Box
                sx={{
                  background: "linear-gradient(135deg, rgba(44, 24, 74, 0.96) 0%, rgba(63, 28, 105, 0.92) 55%, rgba(30, 17, 57, 0.96) 100%)",
                  borderRadius: 4,
                  boxShadow: "0 18px 50px rgba(0,0,0,0.28)",
                  p: 3,
                  border: "1px solid rgba(168, 85, 247, 0.35)",
                  display: "flex",
                  alignItems: { xs: "stretch", sm: "center" },
                  gap: { xs: 0, sm: 2 },
                  color: "white",
                  overflow: "visible",
                }}
              >
                <Avatar
                  src="/images/ai_profiles/tincho.jpg"
                  alt="Tincho"
                  sx={{
                    display: { xs: "none", sm: "flex" },
                    width: 50,
                    height: 50,
                    border: "3px solid rgba(250, 204, 21, 0.95)",
                    boxShadow: "0 12px 24px rgba(0,0,0,0.28)",
                    flexShrink: 0,
                    alignSelf: "flex-start",
                    mt: 0.25,
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: { xs: "flex-start", sm: "center" },
                      justifyContent: "space-between",
                      gap: 1.5,
                      flexWrap: "wrap",
                      mb: 0.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                      <Avatar
                        src="/images/ai_profiles/tincho.jpg"
                        alt="Tincho"
                        sx={{
                          display: { xs: "flex", sm: "none" },
                          width: 42,
                          height: 42,
                          border: "3px solid rgba(250, 204, 21, 0.95)",
                          boxShadow: "0 12px 24px rgba(0,0,0,0.28)",
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="h6" sx={{ mb: 0, fontWeight: 800, letterSpacing: 0.2 }}>
                        {t("undelta_badge_title")}
                      </Typography>
                    </Box>
                    <Chip
                      label="HACKATHON 2026"
                      size="small"
                      sx={{
                        backgroundColor: "rgba(250, 204, 21, 0.18)",
                        color: "#FDE68A",
                        border: "1px solid rgba(250, 204, 21, 0.42)",
                        fontWeight: 700,
                        letterSpacing: 0.35,
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ color: "#e5e5e5", fontWeight: "bold", mb: "5px" }}>
                    {user.profile_name}{t("undelta_badge_text")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#e5e5e5" }}>
                    {t("undelta_badge_text2")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#e5e5e5", mt: 1.5 }}>
                    {t("undelta_badge_text3")}
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 0 }}>
                    <Button
                      variant="outlined"
                      sx={{
                        mt: 2,
                        borderRadius: 999,
                        textTransform: "none",
                        color: "white",
                        borderColor: "#c4b5fd",
                        px: 2,
                        py: 1.4,
                        whiteSpace: "normal",
                        textAlign: "center",
                        fontSize: {
                          xs: "0.85rem",
                          sm: "0.95rem",
                        },
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.1)",
                          borderColor: "#a78bfa",
                        },
                      }}
                      href="/contact?type=early_pitcher"
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "row",
                          gap: 1.5,
                          textAlign: "left",
                          width: "100%",
                        }}
                      >
                        <RecordVoiceOverOutlinedIcon
                          fontSize="small"
                          sx={{ color: "#c4b5fd" }}
                        />
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography sx={{ fontSize: "0.85rem" }}>
                            {t("undelta_early_pitcher_cta")}
                          </Typography>
                        </Box>
                      </Box>
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grow>
        )}
       {/* CARD EARLY PITCHER */}
        {isEarlyPitcher && (
          <Grow in timeout={750}>
            <Box maxWidth="600px" width="100%" sx={{ mb: 3 }}>
              <Box
                sx={{
                  backgroundColor: "rgba(87, 34, 136, 0.7)", // violeta suave
                  borderRadius: 4,
                  boxShadow: 3,
                  p: 3,
                  border: "1px solid rgba(168, 85, 247, 0.6)",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  color: "white",
                }}
              >
                <WorkspacePremiumIcon
                  sx={{
                    mt: 0.5,
                    color: "#FACC15",
                    fontSize: 36,
                    animation: "pulse 2.4s ease-in-out infinite",
                    "@keyframes pulse": {
                      "0%": { transform: "scale(1)" },
                      "50%": { transform: "scale(1.05)" },
                      "100%": { transform: "scale(1)" }
                    }
                  }}
                />                   
                <Box>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    {t("early_pitcher_badge_title")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#e5e5e5", fontWeight: "bold", mb: "5px" }}>
                    {user.profile_name}{t("early_pitcher_badge_text")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#e5e5e5" }}>
                    {t("early_pitcher_badge_text2")}
                  </Typography>
                  {/* Si despues  tenes fecha en billing.early_pitcher_since, podia mostrarla asi: */}
                  {user.early_pitcher_since && (
                    <Typography
                      variant="caption"
                      sx={{ color: "#c4b5fd", display: "block", mt: 1 }}
                    >
                    {t("early_pitcher_since", {
                      date: new Date(user.early_pitcher_since).toLocaleDateString(i18n.language)
                    })}
                    </Typography>
                  )}
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 0 }}>
                    <Button
                      variant="outlined"
                      sx={{
                        mt: 2,
                        borderRadius: 999,
                        textTransform: "none",
                        color: "white",
                        borderColor: "#c4b5fd",
                        ml: -3,
                        mr: -1.5,
                        px: 2,
                        py: 1.4,
                        whiteSpace: "normal", // Permite salto de linea
                        textAlign: "center",  // Centra el texto
                        fontSize: {
                          xs: "0.85rem", // para mobile
                          sm: "0.95rem", // desde tablet en adelante
                        },
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.1)",
                          borderColor: "#a78bfa",
                        },
                      }}
                      href="/contact?type=early_pitcher"
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "row",
                          gap: 1.5,
                          textAlign: "left",
                          width: "100%",
                        }}
                      >
                        <RecordVoiceOverOutlinedIcon
                          fontSize="small"
                          sx={{ color: "#c4b5fd" }}
                        />
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography sx={{ fontSize: "0.85rem" }}>
                            {t("early_pitcher_contact_cta")}
                          </Typography>
                          <Typography sx={{ fontSize: "0.85rem" }}>
                            {t("early_pitcher_contact_cta2")}
                          </Typography>
                        </Box>
                      </Box>
                    </Button>               
                </Box>

                </Box>
              </Box>
            </Box>
          </Grow>
        )}        
        <Grow in timeout={800}>
          <Box maxWidth="600px" width="100%">
            {/* Estado actual */}
            <Typography variant="h5" gutterBottom sx={{ color: "white" }}>
              {t("current_status")}
            </Typography>
            <Box
              sx={{
                backgroundColor: "rgba(0,0,0,0.5)",
                color: "white",
                borderRadius: 4,
                boxShadow: 3,
                p: 3,
                mb: 4,
              }}
            >
              <Typography>
                {t("can_practice")}:{" "}
                <strong>{billing.can_practice ? t("yes") : t("no")}</strong>
              </Typography>
              <Typography>
                {t("available_sessions")}:{" "}
                <strong>{billing.available_sessions}</strong>
              </Typography>
              {billing.current_subscription && (
                <>
                  <Typography sx={{ mt: 1 }}>
                    {t("current_subscription")}:{" "}
                    <strong>
                      {i18n.language === "es"
                        ? billing.current_subscription.plan_name_es
                        : billing.current_subscription.plan_name_en}
                    </strong>
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#ccc" }}>
                      {t("expires")}:{" "}
                      {new Date(billing.current_subscription.end_date).toLocaleDateString(i18n.language)}
                  </Typography>
                </>
              )}

            {/* BOTONES RENOVACION / COMPRA */}
            {/* // Mostrar botones segun estado */}
              {!billing.current_subscription || planExpired ? (
              <Box
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                  borderRadius: 3,
                  p: 3,
                  mt: 4,
                  mb: 2,
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <WarningAmberIcon sx={{ color: "#FBBF24", fontSize: "2rem", mb: 1 }} />
                <Typography sx={{ color: "white", mb: 2 }}>
                  {t("no_active_plan")}
                </Typography>
                <Button
                  variant="contained"
                  href="/pricing"
                  sx={{ borderRadius: 999, textTransform: "none" }}
                >
                  {t("choose_or_renew_plan")}
                </Button>
              </Box>
              ) : billing.available_sessions === 0 ? (
              <Box
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                  borderRadius: 3,
                  p: 3,
                  mt: 4,
                  mb: 2,
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <WarningAmberIcon sx={{ color: "#FBBF24", fontSize: "2rem", mb: 1 }} />
                <Typography sx={{ color: "white", mb: 2 }}>
                  {t("no_sessions_left")}
                </Typography>
                <Button
                  variant="contained"
                  href="/pricing#packs"
                  sx={{ borderRadius: 999, textTransform: "none" }}
                >
                  {t("buy_more_sessions")}
                </Button>
              </Box>

              ) : null}

            </Box>
          </Box>
        </Grow>
        <Grow in timeout={900}>
          <Box maxWidth="600px" width="100%">
            {/* Historial */}
            <Typography variant="h5" gutterBottom sx={{ color: "white" }}>
              {t("purchase_history")}
            </Typography>
            <Divider sx={{ mb: 2, borderColor: "white" }} />
            {billing.history.length === 0 ? (
              <Typography sx={{ color: "white" }}>{t("no_purchases")}</Typography>
            ) : (
              billing.history.map((p, idx) => (
                <Box
                  key={idx}
                  sx={{
                    backgroundColor: "rgba(0,0,0,0.5)",
                    color: "white",
                    borderRadius: 4,
                    boxShadow: 3,
                    p: 3,
                    mb: 2,
                  }}
                >
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {i18n.language === "es" ? p.name_es : p.name_en}
                    </Typography>

                    <Chip
                      label={p.status === "completed" ? t("completed") : t("other")}
                      color={p.status === "completed" ? "success" : "default"}
                      size="small"
                      sx={{ color: "white" }}
                    />
                  </Box>
                    {/* Subdetalle de sesiones para packs y planes */}
                    {
                      <Typography variant="body2" sx={{ opacity: 0.8, fontStyle: "italic" }}>
                        {p.sessions_included} {t("sessions")}
                      </Typography>
                    }                    
                  <Typography variant="body2" sx={{ color: "#ccc" }}>
                      {t("purchase_date")}: {new Date(p.created_at).toLocaleDateString(i18n.language)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#ccc" }}>
                    {t("amount_paid")}: ${p.price_usd}
                    {p.discount_code && (
                      <span style={{ fontStyle: "italic", opacity: 0.7 }}>
                        {" "}({p.discount_code})
                      </span>
                    )}
                  </Typography>
                  {p.expiration && (
                    <Typography variant="body2" sx={{ color: "#ccc" }}>
                      {t("expires")}:  {new Date(p.expiration).toLocaleDateString(i18n.language)}
                    </Typography>
                  )}
                </Box>
              ))
            )}
            {/* </Grow>| */}
          </Box>
        </Grow>
      </AppBackground>
    </Fade>
  );
};

export default Billing;
